import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { RadioOption } from '@hqo/react-components-library/dist/molecules/radioForm';
import { track } from '@hqo/web-tracking';
import { TRACK_EVENT_NAMES, TRACK_EVENT_TYPES } from 'shared/consts';
import { replace } from 'store/router/actions';
import { ResourceBookingEnum } from 'store/app-instance-configs/enums';
import {
  selectAppInstanceConfigs,
  selectCurrentAppInstanceConfigUuid,
  selectCurrentLocationId,
} from 'store/app-instance-configs/selectors';
import {
  isCurrentLocationIdUpdating,
  setSelectedAppInstanceConfigsLocations,
  updateCurrentAppInstanceConfigUuid,
  updateCurrentLocationId,
} from 'store/app-instance-configs/actions';
import { useCurrentAdapterName } from 'hooks/use-current-adapter-name';
import { resetGetResourcesTypes } from 'store/resources/actions';
import { CheckboxOption } from 'components/select-location-modal/select-location.modal-content';
import { AppInstanceConfigWithLocations } from 'store/app-instance-configs/types';
import { isNativeEnabledExchangeConfig } from 'utils/native-enabled-exchange';

interface UseSelectLocationModalReturnValues {
  currentLocationName: string;
  onLocationPickerClick: VoidFunction;
  onRadioButtonClick: (event: ChangeEvent<HTMLInputElement>) => void;
  selectedValue: string;
  onModalClose: VoidFunction;
  options: RadioOption[] | CheckboxOption[];
  onButtonClick: VoidFunction;
  onLocationsChange: (event: ChangeEvent<HTMLInputElement>) => void;
  selectAllChecked: boolean;
  onSelectAllChange: (event: ChangeEvent<HTMLInputElement>) => void;
}

export const useSelectLocationModal = (): UseSelectLocationModalReturnValues => {
  const dispatch = useDispatch();
  const appInstanceConfigs = useSelector(selectAppInstanceConfigs);
  const currentAppInstanceConfigUuid = useSelector(selectCurrentAppInstanceConfigUuid);
  const currentLocationId = useSelector(selectCurrentLocationId);
  const { search, pathname } = useLocation();
  const [selectAllChecked, setSelectAllChecked] = useState(false);
  const { exchangeIntegrationV2 } = useFlags();
  /*
   * RES-1330: with Exchange v2 a native-enabled Exchange config's resources already live in the native
   * config, so offering it as a location would list them twice.
   */
  const appInstanceConfigsWithoutTripleseat = useMemo(() => {
    return appInstanceConfigs.filter(config => {
      return (
        config.vertical_adapter?.name !== ResourceBookingEnum.TripleseatAdapter &&
        !(exchangeIntegrationV2 && isNativeEnabledExchangeConfig(config))
      );
    });
  }, [appInstanceConfigs, exchangeIntegrationV2]);

  const mapLocationIds = useCallback((locationIds: string, mapCallback: (locationId: string) => string) => {
    return locationIds
      ?.split(',')
      .map(locationId => {
        return mapCallback(locationId);
      })
      .filter(locationId => {
        return locationId;
      })
      .join(',');
  }, []);

  const locationsFromPath = useMemo(() => {
    const query = new URLSearchParams(search);

    return query.get('locationId')?.split(',');
  }, [search]);

  const constructCurrentSelectedValue = useCallback(
    (locationIds: string) => {
      return mapLocationIds(locationIds, locationId => {
        const appInstanceConfigUuid = (
          exchangeIntegrationV2 ? appInstanceConfigsWithoutTripleseat : appInstanceConfigs
        ).find(config => config.location_id === locationId)?.uuid;
        return `${appInstanceConfigUuid ?? currentAppInstanceConfigUuid}_${locationId}`;
      });
    },
    [
      appInstanceConfigs,
      appInstanceConfigsWithoutTripleseat,
      currentAppInstanceConfigUuid,
      exchangeIntegrationV2,
      mapLocationIds,
    ],
  );

  const currentSelectedValue = constructCurrentSelectedValue(currentLocationId);
  const [selectedValue, setSelectedValue] = useState('');

  useEffect(() => {
    setSelectedValue(constructCurrentSelectedValue(currentLocationId));
  }, [constructCurrentSelectedValue, currentLocationId, locationsFromPath, search]);

  const currentLocationName = useMemo<string>(() => {
    if (currentLocationId) {
      return mapLocationIds(currentLocationId, locationId => {
        const appInstanceConfig = appInstanceConfigs?.find(config => config.location_id === locationId);

        return appInstanceConfig?.location_name;
      });
    }
    const currentAppInstanceConfig = appInstanceConfigs?.find(
      config => config.uuid === currentAppInstanceConfigUuid && config.location_default,
    );

    return currentAppInstanceConfig?.location_name;
  }, [appInstanceConfigs, currentAppInstanceConfigUuid, currentLocationId, mapLocationIds]);

  const [selectedLocationName, setSelectedLocationName] = useState(currentLocationName);

  const appInstanceConfigUuid = useMemo<string>(() => {
    const selectedValues = selectedValue?.split(',').filter(selectedVal => {
      return selectedVal;
    });

    return !selectedValues
      ? appInstanceConfigs?.[0]?.uuid
      : appInstanceConfigs?.find(config => `${config.uuid}_${config.location_id}` === selectedValues?.[0])?.uuid;
  }, [appInstanceConfigs, selectedValue]);

  const selectedLocationId = useMemo(() => {
    return mapLocationIds(selectedValue, value => {
      return appInstanceConfigs?.find(config => config.location_id && `${config.uuid}_${config.location_id}` === value)
        ?.location_id;
    });
  }, [appInstanceConfigs, mapLocationIds, selectedValue]);

  const updatedQueryParams = useMemo(() => {
    const queryParams = new URLSearchParams(search);

    const selectedLocationIds = selectedLocationId?.split(',').map(id => id.trim()) || [];

    const currentAppInstanceConfigs = appInstanceConfigs.filter(
      config =>
        config.uuid === appInstanceConfigUuid &&
        (selectedLocationIds.length === 0 || selectedLocationIds.includes(config.location_id)),
    );

    const shouldRemoveKey = (key: string, condition: boolean) => {
      if (!condition) queryParams.delete(key);
    };

    shouldRemoveKey(
      'presetWindows',
      currentAppInstanceConfigs.every(config => config.config?.preset_booking_windows_enabled),
    );
    shouldRemoveKey(
      'startDates',
      currentAppInstanceConfigs.every(config => config.config?.multi_day_booking_enabled),
    );

    return queryParams.toString();
  }, [appInstanceConfigUuid, appInstanceConfigs, search, selectedLocationId]);

  const goToPreviousPage = useCallback(() => {
    const removePath = '/select-location-modal';
    const queryParams = updatedQueryParams;
    const backRoutePathName = pathname.substring(0, pathname.indexOf(removePath));
    const backRoute = `${backRoutePathName.length ? backRoutePathName : '/'}?${queryParams}`;
    dispatch(replace(backRoute));
  }, [dispatch, pathname, updatedQueryParams]);

  const options = useMemo<RadioOption[] | CheckboxOption[]>(() => {
    const locationOptions = appInstanceConfigsWithoutTripleseat.map(appInstanceConfig => {
      return {
        label: appInstanceConfig.location_name,
        subtitle: appInstanceConfig.location_address,
        value: `${appInstanceConfig.uuid}_${appInstanceConfig.location_id}`,
        selected: selectedValue?.split(',').includes(`${appInstanceConfig.uuid}_${appInstanceConfig.location_id}`),
        imageUrl: appInstanceConfig?.location_logo_url,
        locationCity: appInstanceConfig?.location_city,
      };
    });

    return locationOptions.sort((a: { label: string }, b: { label: string }) => {
      const locationNameA = a.label.toLowerCase();
      const locationNameB = b.label.toLowerCase();
      if (locationNameA < locationNameB) return -1;
      if (locationNameA > locationNameB) return 1;

      return 0;
    }) as unknown as RadioOption[] | CheckboxOption[];
  }, [appInstanceConfigsWithoutTripleseat, selectedValue]);

  const onLocationPickerClick = useCallback(() => {
    if (pathname === '/') {
      dispatch(replace(`select-location-modal${search}`));
    } else {
      dispatch(replace(`${pathname}/select-location-modal${search}`));
    }
  }, [dispatch, search, pathname]);

  const onModalClose = useCallback(() => {
    setSelectedValue(currentSelectedValue);
    setSelectedLocationName(currentLocationName);
    goToPreviousPage();
    // if not all options are in currentSelectedValue, set selectAllChecked to false
    if (!currentSelectedValue.includes(options.map((option: CheckboxOption) => option.value).join(','))) {
      setSelectAllChecked(false);
    }
  }, [currentLocationName, currentSelectedValue, goToPreviousPage, options]);

  const onRadioButtonClick = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSelectedValue(event.target.value);
    },
    [setSelectedValue],
  );

  const onLocationsChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      let currentSelectedLocationIds = selectedValue?.split(',');
      if (event.target.checked && !currentSelectedLocationIds.includes(event.target.value)) {
        currentSelectedLocationIds.push(event.target.value);
      } else {
        currentSelectedLocationIds = currentSelectedLocationIds.filter(currentLocId => {
          return currentLocId !== event.target.value;
        });
      }
      const selectedLocationIds = currentSelectedLocationIds
        .filter(currentLocId => {
          return currentLocId;
        })
        .join(',');

      setSelectedValue(selectedLocationIds);
      if (!event.target.checked) {
        setSelectAllChecked(false);
      }
    },
    [setSelectedValue, selectedValue],
  );

  const updateSelectedAppInstanceConfigsLocations = useCallback(() => {
    const appInstanceConfigsLocations = (options as CheckboxOption[])
      .filter(option => option.selected)
      .reduce((configsWithLocations, currentOption, _, filteredOptions) => {
        const configUuid = currentOption.value.split('_')[0];
        const configLocations = filteredOptions
          .filter(option => option.value.split('_')[0] === configUuid)
          .map(option => option.value.split('_')[1]);
        const hasConfigLocations = configsWithLocations.some(config => config.uuid === configUuid);

        if (!hasConfigLocations) {
          configsWithLocations.push({ uuid: configUuid, locationIds: configLocations });
        }

        return configsWithLocations;
      }, [] as Array<AppInstanceConfigWithLocations>);

    if (appInstanceConfigsLocations.length > 0) {
      dispatch(setSelectedAppInstanceConfigsLocations(appInstanceConfigsLocations));
    }
  }, [options, dispatch]);

  const onSelectAllChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      setSelectAllChecked(event.target.checked);
      if (selectAllChecked) {
        setSelectAllChecked(false);
      } else setSelectAllChecked(true);
      // If the "select all" checkbox is checked, select all options
      // Otherwise, unselect all options
      const newSelectedValue = event.target.checked
        ? options.map((option: CheckboxOption) => option.value).join(',')
        : '';
      setSelectedValue(newSelectedValue);
    },
    [selectAllChecked, options],
  );

  useEffect(() => {
    // if all options are in selectedValue, set selectAllChecked to true
    if (selectedValue?.split(',').length === options.length) {
      setSelectAllChecked(true);
    } else {
      setSelectAllChecked(false);
    }
  }, [selectedValue, locationsFromPath, search, options, currentLocationId]);

  const currentAdapterName = useCurrentAdapterName();

  const onButtonClick = useCallback(() => {
    track(
      TRACK_EVENT_NAMES.LOCATION_CHANGE_DONE_CLICK,
      {
        type: TRACK_EVENT_TYPES.ACTION,
        service_provider_name: currentAdapterName,
        location_name: selectedLocationName,
      },
      { sendToHqoTracking: true },
    );
    goToPreviousPage();
    dispatch(resetGetResourcesTypes());
    dispatch(updateCurrentLocationId({ currentLocationId: selectedLocationId }));
    dispatch(isCurrentLocationIdUpdating({ isCurrentLocationIdUpdating: true }));
    dispatch(updateCurrentAppInstanceConfigUuid({ currentAppInstanceConfigUuid: appInstanceConfigUuid }));
    updateSelectedAppInstanceConfigsLocations();
  }, [
    currentAdapterName,
    selectedLocationName,
    goToPreviousPage,
    dispatch,
    selectedLocationId,
    appInstanceConfigUuid,
    updateSelectedAppInstanceConfigsLocations,
  ]);

  return {
    currentLocationName,
    onLocationPickerClick,
    onRadioButtonClick,
    selectedValue,
    onModalClose,
    options,
    onButtonClick,
    onLocationsChange,
    selectAllChecked,
    onSelectAllChange,
  };
};
